import { createTheme, MantineProvider, Popover } from "@mantine/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";
import { setLocale } from "yup";

import axiosApi from "~/api/axiosApi.ts";
import { getDataFromResponse } from "~/api/axiosUtils.ts";
import { CommonDataLoadingStatusProvider } from "~/contexts/CommonDataLoadingStatusContext/CommonDataLoadingStatus.tsx";
import { getAuthenticatedUser } from "~/features/auth/api/auth/authApiDispatchers.ts";
import { AUTHENTICATED_USER_QUERY_KEY } from "~/features/auth/api/auth/authQueries.ts";
import LoadingPage from "~/pages/LoadingPage.tsx";
import { getAccessToken } from "~/services/tokenService.ts";
import { getCustomValidationErrorMessages } from "~/utils/form/validationErrorMessages";
import { addCustomValidators } from "~/utils/form/validationRules.ts";

import { routeTree } from "./routeTree.gen";

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}
const accessToken = getAccessToken();
axiosApi.defaults.headers.common["Authorization"] = "Bearer " + accessToken;

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: true,
            refetchOnReconnect: false,
            retry: false,
            staleTime: 86400000,
        },
    },
});

queryClient.setQueryDefaults(AUTHENTICATED_USER_QUERY_KEY, {
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
});

const router = createRouter({
    routeTree,
    defaultPreload: "intent",
    scrollRestoration: true,
    defaultPendingMs: 0, // Show pending immediately
    defaultPendingMinMs: 1000, // Keep it visible for at least 1s
    context: {
        auth: {
            user: null,
        },
        queryClient,
    },
});

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

// Set default to disabled globally
ChartJS.defaults.set("plugins.datalabels", {
    display: false,
});

const App: FunctionComponent = () => {
    const [isAuthInitialized, setIsAuthInitialized] = useState(false);

    useEffect(() => {
        setLocale(getCustomValidationErrorMessages());
        addCustomValidators();

        const token = getAccessToken();

        if (token) {
            // Prefetch using your existing query key and function
            queryClient
                .prefetchQuery({
                    queryKey: AUTHENTICATED_USER_QUERY_KEY,
                    queryFn: () => getAuthenticatedUser().then(getDataFromResponse),
                    staleTime: Infinity,
                })
                .finally(() => {
                    setIsAuthInitialized(true);
                });
        } else {
            // No token, set to null and mark as initialized
            queryClient.setQueryData(AUTHENTICATED_USER_QUERY_KEY, null);
            setIsAuthInitialized(true);
        }
    }, []);

    if (!isAuthInitialized) {
        return <LoadingPage />;
    }

    const theme = createTheme({
        components: {
            Popover: Popover.extend({
                defaultProps: {
                    classNames: {
                        dropdown:
                            "absolute z-40 bg-white shadow-lg rounded-md overflow-hidden dark:bg-gray-800 dark:shadow-none",
                    },
                },
            }),
            InputPlaceholder: {
                defaultProps: {
                    classNames: {
                        placeholder: "text-gray-400",
                    },
                },
            },
        },
    });

    return (
        <QueryClientProvider client={queryClient}>
            <CommonDataLoadingStatusProvider>
                <MantineProvider theme={theme}>
                    <RouterProvider router={router} />
                </MantineProvider>
            </CommonDataLoadingStatusProvider>
            {/*<ReactQueryDevtools initialIsOpen={true} />*/}
        </QueryClientProvider>
    );
};

export default App;
